import { CircleAlert } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { cn } from './cn.js';
import { revealField } from './motion.js';

interface InvalidField {
  element: HTMLElement;
  label: string;
}

function labelOf(element: HTMLElement) {
  const byLabel = (element as HTMLInputElement).labels?.[0]?.textContent;
  // Só a primeira referência é o nome; as seguintes costumam ser o valor escolhido.
  const reference = element.getAttribute('aria-labelledby')?.split(' ')[0];
  const byReference = reference ? document.getElementById(reference)?.textContent : undefined;
  return (byLabel || byReference || element.getAttribute('aria-label') || '').trim();
}

function invalidFields(form: HTMLFormElement): InvalidField[] {
  return [...form.querySelectorAll<HTMLElement>('[aria-invalid="true"]')]
    .map((element) => ({ element, label: labelOf(element) }))
    .filter((field) => field.label);
}

/// Resumo dos campos com erro, ao lado do botão que envia. Salvar não move a tela: cada nome é um
/// atalho que rola até o campo. Lê o próprio formulário — todo campo do design system marca o erro
/// em `aria-invalid` —, então vale para qualquer origem do erro, inclusive a recusa do servidor
/// aplicada a um campo. Só aparece depois da primeira tentativa de envio.
export function FormErrorSummary({
  form: formId,
  className,
}: {
  /// Formulário vigiado quando o resumo fica fora dele, como no rodapé de um diálogo.
  form?: string | undefined;
  className?: string | undefined;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [fields, setFields] = useState<InvalidField[]>([]);

  useEffect(() => {
    const form = formId
      ? document.getElementById(formId)
      : (anchor.current?.closest('form') ?? null);
    if (!(form instanceof HTMLFormElement)) return;
    const read = () => setFields(invalidFields(form));
    const onSubmit = () => setSubmitted(true);
    const observer = new MutationObserver(read);
    observer.observe(form, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-invalid'],
    });
    form.addEventListener('submit', onSubmit, true);
    read();
    return () => {
      observer.disconnect();
      form.removeEventListener('submit', onSubmit, true);
    };
  }, [formId]);

  const shown = submitted && fields.length > 0;
  const heading =
    fields.length === 1
      ? '1 campo precisa de correção: '
      : `${String(fields.length)} campos precisam de correção: `;
  // O anúncio mora num elemento fora do fluxo: vazio, o resumo não pode ocupar lugar nem somar
  // espaçamento na fileira de botões, e uma região de anúncio que some da árvore deixa de falar.
  return (
    <>
      <span ref={anchor} role="status" className="sr-only">
        {shown ? `${heading}${fields.map((field) => field.label).join(', ')}.` : null}
      </span>
      {shown ? (
        <p
          className={cn('flex min-w-0 items-start gap-2 text-xs leading-5 text-danger', className)}
        >
          <CircleAlert className="mt-0.5 size-4 shrink-0" />
          <span>
            {heading}
            {fields.map((field, index) => (
              <Fragment key={index}>
                {index > 0 ? ', ' : null}
                <button
                  type="button"
                  onClick={() => revealField(field.element)}
                  className="rounded font-semibold underline underline-offset-2 transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/60"
                >
                  {field.label}
                </button>
              </Fragment>
            ))}
          </span>
        </p>
      ) : null}
    </>
  );
}
